// src/components/PageForm.jsx
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { petStoreSchema } from "../../schemas/petStoreSchema";
import { FileText, XCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";

export default function PetForm({
  mode,
  defaultValues = {},
  onSubmit,
  onCancel,
  submissionLoading = false,
  fileUpload,
  fileDelete,
}) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(petStoreSchema),
    context: {
      defaultPrice: defaultValues.price || "",
    },
    defaultValues: { ...defaultValues, mode },
  });

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const fileDeleteMutation = useMutation({
    mutationFn: async (url) => await fileDelete(url),
    onSuccess: (data) => {
      setValue("image", "");
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // 🔹 reset the file input field
      }
      toast.success(data?.message || "File deleted successfully");
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to delete sound");
    },
  });

  const handleFileDelete = (url) => {
    if (!url) {
      toast.error("No file to delete");
      return;
    }
    fileDeleteMutation.mutate(url);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium mb-1">Pet Title</label>
        <div className="relative">
          <FileText className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 mr-5" />
          <input
            {...register("title")}
            readOnly={mode === "view"}
            placeholder="Enter Pet title"
            className="pl-10 pr-3 py-2 border rounded w-full"
          />
        </div>
        {errors.title && (
          <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>
        )}
      </div>

      {/* Play SKU Id */}
      <div>
        <label className="block text-sm font-medium mb-1">Play SKU Id</label>
        <input
          {...register("play_sku_id")}
          readOnly={mode === "view"}
          placeholder="Enter SKU Id of Play Store"
          className="pl-3 pr-3 py-2 border rounded w-full"
        />
        {errors.play_sku_id && (
          <p className="text-red-500 text-xs mt-1">
            {errors.play_sku_id.message}
          </p>
        )}
      </div>

      {/* Apple SKU Id */}
      <div>
        <label className="block text-sm font-medium mb-1">Apple SKU Id</label>
        <input
          {...register("apple_sku_id")}
          readOnly={mode === "view"}
          placeholder="Enter SKU Id of Apple Store"
          className="pl-3 pr-3 py-2 border rounded w-full"
        />
        {errors.apple_sku_id && (
          <p className="text-red-500 text-xs mt-1">
            {errors.apple_sku_id.message}
          </p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <textarea
          {...register("description")}
          readOnly={mode === "view"}
          placeholder="Enter description"
          className="pl-3 pr-3 py-2 border rounded w-full"
          rows={3}
        />
        {errors.description && (
          <p className="text-red-500 text-xs mt-1">
            {errors.description.message}
          </p>
        )}
      </div>

      {/* Price */}
      <div>
        <label className="block text-sm font-medium mb-1">Price</label>
        <input
          type="number"
          step="0.01"
          {...register("price")}
          readOnly={mode === "view"}
          placeholder="Enter price"
          className="pl-3 pr-3 py-2 border rounded w-full"
        />
        {errors.price && (
          <p className="text-red-500 text-xs mt-1">{errors.price.message}</p>
        )}
      </div>

      {/* Consent Checkbox */}
      {mode !== "view" && (
        <>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="consent"
              {...register("consent")}
              disabled={mode === "view"}
              className="h-4 w-4 border border-gray-300 rounded"
            />
            <label htmlFor="consent" className="text-sm">
              I agree that I have updated the price in both{" "}
              <a className="text-blue-800" href="https://play.google.com/console/u/0/developers" target="_blank">
                Play store Console{" "}
              </a>
              and Apple store console
            </label>
          </div>
          {errors.consent && (
            <p className="text-red-500 text-xs mt-1">
              {errors.consent.message}
            </p>
          )}
        </>
      )}

      {/* Points */}
      <div>
        <label className="block text-sm font-medium mb-1">Points</label>
        <input
          type="number"
          {...register("points")}
          readOnly={mode === "view"}
          placeholder="Enter points"
          className="pl-3 pr-3 py-2 border rounded w-full"
        />
        {errors.points && (
          <p className="text-red-500 text-xs mt-1">{errors.points.message}</p>
        )}
      </div>

      {/* Image Upload / Preview */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {mode === "view" ? "Image" : "Upload Image"}
        </label>
        <Controller
          name="image"
          control={control}
          render={({ field }) => (
            <>
              {/* Preview existing or uploaded image */}
              {field.value && (
                <div className="mb-2 relative inline-block border border-gray-300 p-2 rounded-md">
                  <img
                    src={field.value}
                    alt="Uploaded"
                    className="max-w-[130px] max-h-64 object-cover rounded-md"
                  />
                  {mode !== "view" && (
                    <XCircle
                      className="absolute -top-1 -right-1 cursor-pointer text-red-500 bg-white rounded-full"
                      size={20}
                      onClick={() => {
                        handleFileDelete(field.value);
                        field.onChange("");
                      }}
                    />
                  )}
                </div>
              )}

              {/* Only show file input when not in view mode */}
              {mode !== "view" && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.tiff,.svg"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const allowedTypes = [
                        "image/jpeg",
                        "image/jpg",
                        "image/png",
                        "image/gif",
                        "image/bmp",
                        "image/webp",
                        "image/tiff",
                        "image/svg+xml",
                      ];
                      if (!allowedTypes.includes(file.type)) {
                        setUploadError(
                          "Only JPG, JPEG, PNG, GIF, BMP, WEBP, TIFF, SVG files are allowed"
                        );
                        return;
                      }
                      setUploading(true);
                      setUploadError(null);
                      try {
                        const formData = new FormData();
                        formData.append("document", file);
                        const response = await fileUpload(formData);
                        const uploadedUrl = response?.data?.url || "";
                        field.onChange(uploadedUrl);
                      } catch (error) {
                        setUploadError(error.message || "File upload failed");
                      } finally {
                        setUploading(false);
                      }
                    }}
                    disabled={uploading}
                    className="border rounded w-full py-2 px-3"
                  />
                  {uploading && (
                    <p className="text-blue-500 text-xs mt-1">Uploading...</p>
                  )}
                  {uploadError && (
                    <p className="text-red-500 text-xs mt-1">{uploadError}</p>
                  )}
                </>
              )}

              {/* No image in view mode */}
              {!field.value && mode === "view" && (
                <p className="text-gray-500 text-sm">No image uploaded</p>
              )}
            </>
          )}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border rounded text-gray-700"
        >
          {mode === "view" ? "Close" : "Cancel"}
        </button>
        {mode !== "view" && (
          <button
            type="submit"
            disabled={submissionLoading}
            className="px-4 py-2 bg-primary text-white rounded flex items-center justify-center gap-2"
          >
            {submissionLoading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                  ></path>
                </svg>
                <span>Loading...</span>
              </>
            ) : (
              <>{mode === "edit" ? "Save Changes" : "Create Pet"}</>
            )}
          </button>
        )}
      </div>
    </form>
  );
}
